import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import axios from 'axios';
import { BASE_API_URL } from './api';
import { isNetworkLevelError } from '../utils/rechargeProxyMode';
import { cachedFetch, invalidateSharedCache } from './sharedCache';

// On a network-level failure (connection died before any HTTP response —
// WebKit's "Load failed" class) or a 502/504 answered by the edge, retry the
// identical request through our first-party passthrough. Real responses,
// including GraphQL userErrors, pass through untouched; healthy connections
// never hit the proxy.
const _shopifyProxyUrl = (url) =>
  String(url).replace(/^https:\/\/[^/]+/, `${BASE_API_URL}/api/shopify-storefront-proxy`);

const fetchWithProxyFallback = async (url, init) => {
  let response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    if (!isNetworkLevelError(error) || _shopifyProxyUrl(url) === String(url)) throw error;
    return fetch(_shopifyProxyUrl(url), init);
  }
  if ((response.status === 502 || response.status === 504) && _shopifyProxyUrl(url) !== String(url)) {
    try {
      return await fetch(_shopifyProxyUrl(url), init);
    } catch (proxyError) {
      return response; // keep the original gateway response if the proxy is unreachable
    }
  }
  return response;
};

const storefrontClient = createStorefrontApiClient({
  storeDomain: process.env.REACT_APP_SHOPIFY_STORE_DOMAIN,
  apiVersion: '2026-01',
  publicAccessToken: process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  customFetchApi: fetchWithProxyFallback,
});

// Utility function to clean Shopify GraphQL IDs
const cleanShopifyId = (id) => {
  return id ? id.split('/').pop() : id;
};

// ---------------------------------------------------------------------------
// Shared cache (see sharedCache.js)
// ---------------------------------------------------------------------------
// Shopify reads route through the app-wide query cache. Product data rarely
// changes, so it stays fresh for 5 minutes — this prevents re-fetching all
// 250+ products every time a user navigates between pages.
const SHOPIFY_CACHE_TTL_MS = 5 * 60_000; // 5 minutes
const SHOPIFY_CUSTOMER_TTL_MS = 60_000;

const deduplicatedShopifyFetch = (queryKey, fetcher, ttlMs = SHOPIFY_CACHE_TTL_MS) =>
  cachedFetch(queryKey, fetcher, ttlMs);

// Function to fetch products from a specific collection
export const getProductsByCollection = async (collectionHandle) => {
  try {
    const query = `
      query GetProductsByCollection($handle: String!) {
        collection(handle: $handle) {
          title
          products(first: 100) {
            edges {
              node {
                id
                title
                handle
                tags
                description
                productType
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                images(first: 5) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
                metafields(identifiers: [
                  { namespace: "product_info", key: "instructions" },
                  { namespace: "custom", key: "faqs" }
                ]) {
                  namespace
                  key
                  value
                  references(first: 20) {
                    edges {
                      node {
                        ... on Metaobject {
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
                variants(first: 250) {
                  edges {
                    node {
                      id
                      sku
                      title
                      barcode
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await storefrontClient.request(query, {
      variables: {
        handle: collectionHandle,
      },
    });

    // Clean the response data to remove the gid:// prefix from IDs
    const collection = response.data.collection;
    if (collection && collection.products && collection.products.edges) {
      collection.products.edges = collection.products.edges.map(edge => {
        const node = edge.node;

        // Clean product ID
        node.id = cleanShopifyId(node.id);

        // Flatten variants array similar to getAllProducts
        if (node.variants && node.variants.edges) {
          const variants = node.variants.edges.map(variantEdge => {
            const variantNode = variantEdge.node;
            variantNode.id = cleanShopifyId(variantNode.id);
            return variantNode;
          });
          node.variants = variants;
        }

        return { node };
      });
    }

    return collection;
  } catch (error) {
    console.error('Error fetching products from collection:', error);
    throw error;
  }
};

// Function to fetch all products with pagination
export const getAllProducts = async () => {
  return deduplicatedShopifyFetch(['allProducts'], async () => {
    try {
      let allProducts = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const query = `
        query GetAllProducts($cursor: String) {
          products(first: 250, after: $cursor) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                description
                descriptionHtml
                tags
                productType
                availableForSale
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                images(first: 5) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
                metafields(identifiers: [
                  { namespace: "product_info", key: "instructions" },
                  { namespace: "custom", key: "gift_curated_order" },
                  { namespace: "custom", key: "faqs" }
                ]) {
                  namespace
                  key
                  value
                  references(first: 20) {
                    edges {
                      node {
                        ... on Metaobject {
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
                variants(first: 250) {
                  edges {
                    node {
                      id
                      sku
                      title
                      barcode
                      price {
                        amount
                      }
                      compareAtPrice {
                        amount
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await storefrontClient.request(query, {
        variables: { cursor }
      });
      
      // Check if the response has the expected structure
      if (!response.data || !response.data.products || !response.data.products.edges) {
        console.error('Unexpected response structure:', response);
        throw new Error('Invalid response structure from Shopify GraphQL API');
      }

      const { products } = response.data;
      const { pageInfo, edges } = products;

      // Process and add products from this page
      const pageProducts = edges
        // .filter(edge => edge.node.availableForSale) // Temporarily removed - field not in response
        .map(edge => {
          const node = edge.node;
          
          // Clean product ID
          node.id = cleanShopifyId(node.id);
          
          // Clean variant IDs and map to simplified structure
          const variants = node.variants.edges.map(variantEdge => {
            const variantNode = variantEdge.node;
            variantNode.id = cleanShopifyId(variantNode.id);
            return variantNode;
          });
          
          return {
            ...node,
            variants
          };
        });

      allProducts = [...allProducts, ...pageProducts];

      // Update pagination variables
      hasNextPage = pageInfo.hasNextPage;
      cursor = pageInfo.endCursor;
    }

    return allProducts;
  } catch (error) {
    console.error('Error fetching all products:', error);
    throw error;
  }
  });
};

// Fetch nutrition_data metafield for a single product (lazy-loaded). Bundle
// products (e.g. Croissants Box) often have no nutrition_data metafield of
// their own even though every item inside them does, so when the direct
// lookup comes back empty and a productId is available, fall back to the
// backend, which assembles the bundle's per-item data (see
// wildgrain-api product/service.js getProductNutritionData).
export const getProductNutritionData = async (handle, productId) => {
  return deduplicatedShopifyFetch(['nutrition', handle], async () => {
    const query = `
      query GetProductNutrition($handle: String!) {
        product(handle: $handle) {
          metafield(namespace: "custom", key: "nutrition_data") {
            value
          }
        }
      }
    `;
    const response = await storefrontClient.request(query, {
      variables: { handle },
    });
    const value = response.data?.product?.metafield?.value || null;
    if (value || !productId) return value;

    try {
      const cleanId = String(productId).replace('gid://shopify/Product/', '');
      const url = `${BASE_API_URL}/api/product/details/${cleanId}/nutrition`;
      const { data } = await axios.get(url);
      return data?.nutritionData ? JSON.stringify(data.nutritionData) : null;
    } catch (error) {
      console.error('Error fetching fallback bundle nutrition data:', error);
      return null;
    }
  });
};

// Function to create Customer Access Token (customer login)
export const customerAccessTokenCreate = async (email, password) => {
  try {
    const mutation = `
      mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input: $input) {
          customerAccessToken {
            accessToken
            expiresAt
          }
          customerUserErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        email,
        password,
      },
    };

    const response = await storefrontClient.request(mutation, { variables });

    const result = response.data?.customerAccessTokenCreate;

    if (!result) {
      throw new Error('Unexpected response from Shopify');
    }

    const { customerAccessToken, customerUserErrors } = result;

    if (Array.isArray(customerUserErrors) && customerUserErrors.length) {
      throw new Error(customerUserErrors.map((e) => e.message).join(', '));
    }

    if (!customerAccessToken?.accessToken) {
      throw new Error('Login failed');
    }

    return customerAccessToken;
  } catch (error) {
    console.error('Error creating customer access token:', error);
    throw error;
  }
};

// Function to extend an existing customer access token (see utils/sessionRefresh.js).
// Resolves to null rather than throwing — renewal is always best-effort.
export const customerAccessTokenRenew = async (accessToken) => {
  try {
    const mutation = `
      mutation customerAccessTokenRenew($customerAccessToken: String!) {
        customerAccessTokenRenew(customerAccessToken: $customerAccessToken) {
          customerAccessToken {
            accessToken
            expiresAt
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const response = await storefrontClient.request(mutation, {
      variables: { customerAccessToken: accessToken },
    });

    const result = response.data?.customerAccessTokenRenew;

    if (Array.isArray(result?.userErrors) && result.userErrors.length) {
      console.warn(
        'Shopify declined customer access token renewal:',
        result.userErrors.map((e) => e.message).join(', ')
      );
      return null;
    }

    return result?.customerAccessToken || null;
  } catch (error) {
    console.warn('Error renewing customer access token:', error);
    return null;
  }
};

// Function to initiate customer password recovery (forgot password)
export const customerRecover = async (email) => {
  try {
    const mutation = `mutation customerRecover($email: String!) {\n      customerRecover(email: $email) {\n        customerUserErrors {\n          field\n          message\n        }\n      }\n    }`;

    const variables = { email };

    const response = await storefrontClient.request(mutation, { variables });

    const result = response.data?.customerRecover;

    if (!result) {
      throw new Error('Unexpected response from Shopify');
    }

    const { customerUserErrors } = result;

    if (Array.isArray(customerUserErrors) && customerUserErrors.length) {
      throw new Error(customerUserErrors.map((e) => e.message).join(', '));
    }

    // If no user errors, assume success
    return true;
  } catch (error) {
    console.error('Error sending password recovery email:', error);
    throw error;
  }
};

// -----------------------------------------------------------------------------
// Customer Details
// -----------------------------------------------------------------------------

// Module-level cache so consumers share one Storefront roundtrip per page load.
let _customerTagsPromise = null;

// Returns Shopify customer tags using the access token from localStorage.
// Resolves to [] on missing token / failure (fail-closed).
export const getCustomerTags = async () => {
  if (_customerTagsPromise) return _customerTagsPromise;

  _customerTagsPromise = (async () => {
    try {
      const tokenStr = typeof window !== 'undefined'
        ? localStorage.getItem('shopifyCustomerAccessToken')
        : null;
      if (!tokenStr) return [];
      const tokenObj = JSON.parse(tokenStr);
      const accessToken = tokenObj?.accessToken;
      if (!accessToken) return [];

      const customer = await getCustomer(accessToken);
      return Array.isArray(customer?.tags) ? customer.tags : [];
    } catch (err) {
      console.warn('[getCustomerTags] failed to load tags:', err);
      return [];
    }
  })();

  return _customerTagsPromise;
};

export const clearCustomerTagsCache = () => {
  _customerTagsPromise = null;
};

// Fetch detailed customer information using their Shopify customer access token.
// Navigation, Account, and Impact all request this on mount, so it is cached
// briefly to collapse the duplicate first-paint queries.
export const getCustomer = async (customerAccessToken) => {
  return deduplicatedShopifyFetch(['shopifyCustomer', customerAccessToken], async () => {
    try {
      const query = `
        query getCustomer($token: String!) {
          customer(customerAccessToken: $token) {
            id
            firstName
            lastName
            acceptsMarketing
            email
            phone
            tags
            numberOfOrders
            createdAt
            updatedAt
            defaultAddress {
              firstName
              lastName
              address1
              address2
              city
              province
              provinceCode
              zip
              country
              countryCode
            }
          }
        }
      `;

      const response = await storefrontClient.request(query, {
        variables: { token: customerAccessToken },
      });

      return response.data?.customer;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      throw error;
    }
  }, SHOPIFY_CUSTOMER_TTL_MS);
};

// -----------------------------------------------------------------------------
// Orders (Order History)
// -----------------------------------------------------------------------------

// Orders change more often than products (e.g. right after checkout), so use a
// short TTL — just enough to collapse the duplicate requests Overview and
// SubscriptionDetail issue while bootstrapping the same page.
const ORDERS_CACHE_TTL_MS = 60_000; // 1 minute

export const getCustomerOrders = (customerAccessToken, cursor = null, first = 10) =>
  deduplicatedShopifyFetch(
    ['customerOrders', first, cursor || '', customerAccessToken],
    () => fetchCustomerOrdersUncached(customerAccessToken, cursor, first),
    ORDERS_CACHE_TTL_MS
  );

export const invalidateCustomerOrdersCache = () => invalidateSharedCache('customerOrders');

// An order counts as "the one just placed" if Shopify processed it this
// recently. Generous on purpose: it only has to beat the member's previous
// order, which is days apart, never minutes.
const JUST_PLACED_WINDOW_MS = 10 * 60_000;

// After "Order Today": Recharge creates the Shopify order a moment after the
// charge succeeds, and the customer's orders are cached for a minute, so a
// single refetch right after the order still shows the previous one and the
// "Last order" card only appeared after a manual reload. Re-read (uncached)
// until an order processed in the last few minutes shows up. Bounded: after
// attempts × intervalMs (~30s) it hands back whatever the last read returned
// (or null when every read failed) so callers can still render.
export const waitForNewCustomerOrder = async (
  customerAccessToken,
  first = 5,
  { attempts = 10, intervalMs = 3000 } = {}
) => {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  let latest = null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(intervalMs);
    invalidateCustomerOrdersCache();
    try {
      latest = await getCustomerOrders(customerAccessToken, null, first);
    } catch (err) {
      console.warn('waitForNewCustomerOrder: retrying after read error', err);
      continue;
    }
    const newest = latest?.orders?.[0];
    if (newest?.processedAt && Date.now() - new Date(newest.processedAt).getTime() <= JUST_PLACED_WINDOW_MS) {
      return latest;
    }
  }
  return latest;
};

const fetchCustomerOrdersUncached = async (customerAccessToken, cursor = null, first = 10) => {
  try {
    const query = `
      query getRecentOrders($token: String!, $cursor: String) {
        customer(customerAccessToken: $token) {
          orders(first: ${first}, after: $cursor, sortKey: ID, reverse: true) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                name
                processedAt
                canceledAt
                fulfillmentStatus
                financialStatus
                statusUrl
                successfulFulfillments {
                  trackingInfo {
                    number
                    url
                  }
                  trackingCompany
                }
                totalPrice {
                  amount
                  currencyCode
                }
                lineItems(first: 50) {
                  edges {
                    node {
                      title
                      quantity
                      variant {
                        id
                        sku
                        image {
                          url
                        }
                        product {
                          id
                          featuredImage {
                            url
                          }
                        }
                      }
                    }
                  }
                }
                customAttributes {
                  key
                  value
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      token: customerAccessToken,
      cursor,
    };

    const response = await storefrontClient.request(query, { variables });

    const customer = response.data?.customer;
    if (!customer) return { orders: [], pageInfo: { hasNextPage: false, endCursor: null } };

    const ordersConnection = customer.orders;
    const { pageInfo, edges } = ordersConnection;

    // Map edges to simplified orders array
    const orders = edges.map((edge) => {
      const orderNode = { ...edge.node };
      orderNode.cursor = edge.cursor;
      // Clean Shopify ID (orderNode.id) incase needed
      orderNode.id = cleanShopifyId(orderNode.id);
      return orderNode;
    });

    return { orders, pageInfo };
  } catch (error) {
    console.error('Error fetching customer orders:', error);
    throw error;
  }
}; 